import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Loading } from "../../Common/OtherElements/Loading";
import allImages from "../../../assets/images-import";
import {
  getSectionName,
  getMappingItemId,
  getContentTitle,
  getCategoryName,
  getTopicName,
  getImageUrl,
  getPublishedOn,
  isTruthyFlag,
} from "./sectionMappingUtils";

const renderFlagBadge = (isOn, onLabel, offLabel) => (
  <span className={`badge badge-soft-${isOn ? "success" : "secondary"}`}>
    {isOn ? onLabel : offLabel}
  </span>
);

const MappedContentsPanel = ({
  selectedSection,
  mappedItems,
  loading,
  reordering,
  onAddContent,
  onRemove,
  onReorder,
  canAdd,
  canDelete,
  canReorder,
}) => {
  const handleDragEnd = (result) => {
    if (!result.destination || !canReorder || reordering) return;
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (sourceIndex === destIndex) return;

    const reordered = [...mappedItems];
    const [removed] = reordered.splice(sourceIndex, 1);
    reordered.splice(destIndex, 0, removed);
    onReorder(reordered);
  };

  if (!selectedSection) {
    return (
      <div className="card h-100">
        <div className="card-body d-flex flex-column align-items-center justify-content-center text-center py-5">
          <i
            className="ri-layout-masonry-line text-muted mb-3"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="text-muted">Select a section</h5>
          <p className="text-muted mb-0">
            Choose a homepage section from the left panel to view and manage
            mapped contents.
          </p>
        </div>
      </div>
    );
  }

  const renderRow = (item, index, dragHandleProps = null) => {
    const isFeatured = isTruthyFlag(item, "isFeatured", "IsFeatured");
    const isPremium = isTruthyFlag(item, "isPremium", "IsPremium");

    return (
      <>
        <td className="text-center text-muted" {...dragHandleProps}>
          {canReorder ? (
            <i className="ri-drag-move-2-fill fs-5"></i>
          ) : (
            "—"
          )}
        </td>
        <td>
          <img
            src={getImageUrl(item) || allImages.DefultImage}
            alt=""
            className="rounded"
            style={{ width: 44, height: 44, objectFit: "cover" }}
            onError={(e) => {
              e.target.src = allImages.DefultImage;
            }}
          />
        </td>
        <td>{getContentTitle(item)}</td>
        <td>{getCategoryName(item)}</td>
        <td>{getTopicName(item)}</td>
        <td>{renderFlagBadge(isFeatured, "Yes", "No")}</td>
        <td>{renderFlagBadge(isPremium, "Premium", "Std")}</td>
        <td>{getPublishedOn(item)}</td>
        <td className="text-center fw-semibold">{index + 1}</td>
        <td>
          {canDelete ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              title="Remove from section"
              onClick={() => onRemove(getMappingItemId(item))}
              disabled={reordering}
            >
              <i className="ri-delete-bin-6-line"></i>
            </button>
          ) : (
            "—"
          )}
        </td>
      </>
    );
  };

  return (
    <div className="card h-100">
      <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <h5 className="mb-0">{getSectionName(selectedSection)}</h5>
          <p className="text-muted small mb-0 mt-1">
            Drag rows to reorder homepage display
          </p>
        </div>
        {canAdd ? (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onAddContent}
          >
            <i className="ri-add-line me-1"></i>
            Add Content
          </button>
        ) : null}
      </div>
      <div className="card-body manage-amenity-master-card-body">
        {loading ? (
          <Loading />
        ) : mappedItems.length === 0 ? (
          <div className="text-center py-5">
            <i className="ri-inbox-line text-muted mb-2 d-block fs-1"></i>
            <p className="text-muted mb-3">No contents mapped yet</p>
            {canAdd ? (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={onAddContent}
              >
                Add Content
              </button>
            ) : null}
          </div>
        ) : (
          <>
            {reordering ? (
              <p className="text-muted small mb-2">
                <span className="spinner-border spinner-border-sm me-1"></span>
                Saving order...
              </p>
            ) : null}
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="table-responsive">
                <table className="table align-middle table-bordered mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}></th>
                      <th>Image</th>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Topic</th>
                      <th>Featured</th>
                      <th>Premium</th>
                      <th>Published On</th>
                      <th style={{ width: 70 }}>Order</th>
                      <th style={{ width: 80 }}>Action</th>
                    </tr>
                  </thead>
                  <Droppable droppableId="mapped-contents">
                    {(provided) => (
                      <tbody
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {mappedItems.map((item, index) => (
                          <Draggable
                            key={String(getMappingItemId(item))}
                            draggableId={String(getMappingItemId(item))}
                            index={index}
                            isDragDisabled={!canReorder || reordering}
                          >
                            {(dragProvided, snapshot) => (
                              <tr
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                className={
                                  snapshot.isDragging ? "table-active" : ""
                                }
                                style={{
                                  ...dragProvided.draggableProps.style,
                                  cursor: canReorder ? "grab" : "default",
                                }}
                              >
                                {renderRow(
                                  item,
                                  index,
                                  dragProvided.dragHandleProps
                                )}
                              </tr>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </tbody>
                    )}
                  </Droppable>
                </table>
              </div>
            </DragDropContext>
          </>
        )}
      </div>
    </div>
  );
};

export default MappedContentsPanel;
